import { useEffect, useState } from 'react'
import { Gift } from 'lucide-react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { referralsService, type PartnerPromotion } from '@/services/referralsService'
import { formatPremioBrl, mesPremioLabel } from '@/pages/parceiros/parceirosPremio'
import { indicadorTipoLabel } from '@/pages/indicacoes/indicadorTipos'

type Participant = {
  parceiroId: string
  nome: string
  empresa: string | null
  tipo: string | null
  indicacoes: number
  valorFechado: number
  meta: number
  pct: number
}

export function PromotionParticipantsSection() {
  const [participants, setParticipants] = useState<Participant[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    referralsService
      .promotionParticipants()
      .then((data) => {
        if (!cancelled) setParticipants(data.participants)
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <section className='glass-card mt-4 overflow-hidden p-0'>
      <div className='border-b border-[var(--glass-border)] px-4 py-3'>
        <h2 className='text-base font-semibold text-text-primary'>Quem está participando</h2>
      </div>
      <ParticipantsTable participants={participants} loading={loading} />
    </section>
  )
}

function ParticipantsTable({ participants, loading }: { participants: Participant[]; loading: boolean }) {
  return (
    <table className='w-full text-left text-sm'>
      <thead className='text-[var(--font-color-label)]'>
        <tr>
          {['Parceiro', 'Tipo', 'Indicações', 'Fechado', 'Meta'].map((header) => (
            <th key={header} className='px-4 py-3 text-xs font-medium uppercase tracking-wider'>{header}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {participants.map((item) => (
          <tr key={item.parceiroId} className='border-t border-[var(--glass-border)]'>
            <td className='px-4 py-3'>
              <p className='font-medium text-text-primary'>{item.nome}</p>
              {item.empresa && item.empresa !== item.nome && (
                <p className='text-xs text-text-muted'>{item.empresa}</p>
              )}
            </td>
            <td className='px-4 py-3 text-text-muted'>{indicadorTipoLabel(item.tipo)}</td>
            <td className='px-4 py-3'>{item.indicacoes}</td>
            <td className='px-4 py-3'>{formatPremioBrl(item.valorFechado)}</td>
            <td className='px-4 py-3 text-text-muted'>{item.pct}%</td>
          </tr>
        ))}
        {!loading && participants.length === 0 && (
          <tr>
            <td colSpan={5} className='px-4 py-6 text-sm text-text-muted'>
              Nenhum parceiro com indicação nesta campanha.
            </td>
          </tr>
        )}
      </tbody>
    </table>
  )
}

export function PromotionVigenteView() {
  const [promotion, setPromotion] = useState<PartnerPromotion | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    referralsService
      .promotionParticipants()
      .then((data) => {
        if (cancelled) return
        setPromotion(data.promotion)
      })
      .catch(() => {
        if (!cancelled) setError('Não foi possível carregar a promoção vigente.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <DashboardLayout title='Promoções'>
      <div className='pb-20 md:pb-6'>
        <p className='text-xs font-semibold uppercase tracking-[0.2em] text-text-muted'>Parceiros</p>
        <h1 className='mt-1 text-2xl font-bold text-text-primary'>Promoção vigente</h1>
        <p className='mt-2 max-w-2xl text-sm text-text-muted'>
          Campanha ativa no portal e os parceiros que estão participando.
        </p>

        {error && <p className='mt-4 text-sm text-rose-400'>{error}</p>}
        {loading && <p className='mt-6 text-sm text-text-muted'>Carregando...</p>}

        {!loading && !promotion && (
          <p className='mt-6 text-sm text-text-muted'>Nenhuma promoção vigente no momento.</p>
        )}

        {promotion && (
          <section className='glass-card mt-6 overflow-hidden'>
            <div className='grid gap-0 lg:grid-cols-[minmax(240px,360px)_1fr]'>
              {promotion.imageUrl ? (
                <img
                  src={promotion.imageUrl}
                  alt={promotion.rewardName}
                  className='h-full max-h-80 w-full bg-white object-contain'
                />
              ) : (
                <div className='flex min-h-48 items-center justify-center bg-white/[0.03] text-text-muted'>
                  <Gift size={28} />
                </div>
              )}
              <div className='p-5'>
                <p className='text-[11px] font-semibold uppercase tracking-wide text-text-muted'>
                  {promotion.title} · {mesPremioLabel(promotion)}
                </p>
                <h2 className='mt-1 text-2xl font-bold text-text-primary'>{promotion.rewardName}</h2>
                <p className='mt-2 text-sm text-text-muted'>{promotion.bannerText || promotion.description}</p>
                <p className='mt-4 text-sm font-medium text-text-primary'>
                  Meta {formatPremioBrl(promotion.targetValue)}
                </p>
              </div>
            </div>
          </section>
        )}

        {promotion && <PromotionParticipantsSection />}
      </div>
    </DashboardLayout>
  )
}
